import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';

// Whether the account's owner has ever had the mobile app — the one thing the home's
// download-the-app card is gated on. See `pages/api/mobile-app-status.ts`: the signal is a USER
// message sent from the app (or a subscribed push registration), not merely an APP-tagged chat.

/** true = has it · false = never had it · null = we could not find out. */
export type MobileAppInstalled = boolean | null;

async function fetchMobileAppStatus(): Promise<MobileAppInstalled> {
  const res = await apiFetch(`${API_BASE}/api/mobile-app-status`, { credentials: 'include' });
  if (!res.ok) return null;
  const data = (await res.json()) as { installed?: MobileAppInstalled };
  return typeof data.installed === 'boolean' ? data.installed : null;
}

/**
 * The gate, as a pure function of the query's state — ONE rule, in dev and in production.
 *
 * Only a CONFIRMED "never had it" shows the card. Loading is not a no, and neither is `null`: the
 * probe answers `null` whenever it could not find out (the conversation store unreachable, the
 * bridge down, a deployed server predating the action), and treating that as "never installed" would
 * advertise the app to the very people already holding it — for a whole outage, to everyone.
 *
 * There is deliberately no dev exception. One existed, so that the card would render on localhost
 * before the bridge action shipped, and it did the exact damage this rule exists to prevent: every
 * local session saw the card, including accounts that plainly have the app. To *see* the card, sign
 * in as an account that has never installed it — Dev Tools ▸ Demo accounts switches the whole app to
 * one — rather than making the gate lie.
 */
/**
 * Taken from the query rather than spelled out, so the not-yet-settled state can't be named wrongly:
 * React Query calls it `loading` here and `pending` in v5, and a hand-written literal that says
 * `pending` compiles into a test asserting a state the library never emits — a "loading is covered"
 * that covers nothing. An upgrade now surfaces at the call sites instead.
 */
export type MobileAppStatusState = UseQueryResult['status'];

export function showMobileAppCard(status: MobileAppStatusState, installed: MobileAppInstalled): boolean {
  return status === 'success' && installed === false;
}

// One query config, shared by both hooks so the key + staleTime can't drift. React Query
// dedupes the two callers by key, so mounting both costs a single request.
function useMobileAppStatusQuery(): UseQueryResult<MobileAppInstalled> {
  return useQuery({
    queryKey: queryKeys.mobileAppStatus,
    queryFn: fetchMobileAppStatus,
    // The answer changes at most once in an account's life; don't re-ask on every home visit.
    staleTime: 60 * 60 * 1000,
  });
}

/** Whether to offer the download-the-app card. See `showMobileAppCard` for the rule. */
export function useShowMobileAppCard(): boolean {
  const query = useMobileAppStatusQuery();
  return showMobileAppCard(query.status, query.data ?? null);
}

/**
 * The raw install verdict — `true` has it · `false` no app-sent message and no push registration ·
 * `null` unknown/loading.
 *
 * Unlike `useShowMobileAppCard` (a card gated on a CONFIRMED "never had it"), the app banner
 * always shows and only *labels its CTA* from this: Open when we know the app is installed,
 * Get otherwise. `null` therefore defaults to Get — the acquisition CTA is the safe fallback
 * when we could not find out, exactly as the smart link itself device-detects and does the
 * right thing regardless.
 */
export function useMobileAppInstalled(): MobileAppInstalled {
  const query = useMobileAppStatusQuery();
  return query.data ?? null;
}
